import { Injectable } from '@angular/core';
import { BaseHttpService } from '@core/http/base-http.service';
import { Config } from '@shared/configs/config';
import { normalizeApiDateValue } from '@shared/helpers/date-format.helper';
import { catchError, EMPTY, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ProjectPipelineAPIService extends BaseHttpService {
  getProjectsPipeline() {
    const url = `${Config.API}/project/pipeline`;
    return this.get(url);
  }

  createProjectsPipeline(payload: any) {
    const url = `${Config.API}/project/pipeline`;
    return this.post(url, payload);
  }

  removeManager(id: number) {
    const url = `${Config.API}/project/pipeline/${id}`;
    return this.delete(url).pipe(
      tap(() => this.s.success(this.t.instant('ProjectResources.managerEdit.managerRemoveSuccess'))),
      catchError(() => {
        this.s.error(this.t.instant('ProjectResources.managerEdit.managerRemoveError'));
        return EMPTY;
      })
    );
  }

  togglePipeline(id: number, active: boolean) {
    const url = `${Config.API}/project/pipeline/${id}/active`;
    return this.patch(url, { active }).pipe(
      tap(() => this.s.success(this.t.instant('ProjectResources.managerEdit.timelineToggleSuccess'))),
      catchError(() => {
        this.s.error(this.t.instant('ProjectResources.managerEdit.timelineToggleError'));
        return EMPTY;
      })
    );
  }

  addProjectToPipeline(
    timelineId: number | string,
    {
      id,
      termStart,
      termEnd,
      state = 'current'
    }: {
      id: number;
      termStart: string;
      termEnd: string;
      state?: 'current' | 'forecast';
    }
  ) {
    const url = `${Config.API}/project/pipeline/${timelineId}/entry`;
    return this.put(url, {
      projectId: id,
      termStart: normalizeApiDateValue(termStart),
      termEnd: normalizeApiDateValue(termEnd),
      state
    }).pipe(
      tap(() => this.s.success(this.t.instant('ProjectResources.projectAddSuccess'))),
      catchError(() => {
        this.s.success(this.t.instant('ProjectResources.projectAddError'));
        return EMPTY;
      })
    );
  }

  changeProjectPipeline(projectId: number, timelineId: number) {
    const url = `${Config.API}/project/pipeline/${timelineId}/entry/${projectId}/move`;
    return this.patch(url, {});
  }

  removeProjectFromPipeline(pipelineId: number, projectId: number) {
    const url = `${Config.API}/project/pipeline/${pipelineId}/entry/${projectId}`;
    return this.delete(url, false).pipe(
      tap(() => this.s.success(this.t.instant('ProjectResources.projectRemove.removeSuccess'))),
      catchError(() => {
        this.s.error(this.t.instant('ProjectResources.projectRemove.removeError'));
        return EMPTY;
      })
    );
  }

  removeProjectEntryFromPipeline(pipelineId: number, entryId: number) {
    const url = `${Config.API}/project/pipeline/${pipelineId}/entry/${entryId}`;
    return this.delete(url, false).pipe(
      tap(() => this.s.success(this.t.instant('ProjectResources.projectRemove.removeSuccess'))),
      catchError(() => {
        this.s.error(this.t.instant('ProjectResources.projectRemove.removeError'));
        return EMPTY;
      })
    );
  }

  transformProjectEntry(pipelineId: number, entryId: number, payload: { termStart: string; termEnd: string }) {
    const url = `${Config.API}/project/pipeline/${pipelineId}/entry/${entryId}/transform`;
    return this.patch(url, payload);
  }

  addTimelineToPipeline(pipelineId: number, pipelineCount: number) {
    const url = `${Config.API}/project/pipeline/${pipelineId}`;
    return this.post(url, { pipelineCount }).pipe(
      tap(() => this.s.success(this.t.instant('ProjectResources.managerEdit.timelineToggleSuccess'))),
      catchError(() => {
        this.s.error(this.t.instant('ProjectResources.managerEdit.timelineToggleError'));
        return EMPTY;
      })
    );
  }

  updateTeam(teamId: number, payload: any) {
    const url = `${Config.API}/project/pipeline/resource/team/${teamId}`;
    return this.put(url, payload).pipe(
      tap(() => {
        this.s.success(this.t.instant('ProjectResources.newTeam.editTeamSuccess'));
      }),
      catchError(() => {
        this.s.error(this.t.instant('ProjectResources.newTeam.editTeamError'));
        return EMPTY;
      })
    );
  }

  createTeam(payload: any) {
    const url = `${Config.API}/project/pipeline/resource/team`;
    return this.post(url, payload).pipe(
      tap(() => {
        this.s.success(this.t.instant('ProjectResources.newTeam.addTeamSuccess'));
      }),
      catchError(() => {
        this.s.error(this.t.instant('ProjectResources.newTeam.addTeamError'));
        return EMPTY;
      })
    );
  }

  removeTeam(teamId: number) {
    const url = `${Config.API}/project/pipeline/resource/team/${teamId}`;
    return this.delete(url).pipe(
      tap(() => {
        this.s.success(this.t.instant('ProjectResources.newTeam.removeTeamSuccess'));
      }),
      catchError(() => {
        this.s.error(this.t.instant('ProjectResources.newTeam.removeTeamError'));
        return EMPTY;
      })
    );
  }

  removeTeamFromStage(stageId: number) {
    const url = `${Config.API}/project/pipeline/timeline-change/${stageId}/team`;
    return this.patch(url, { teamId: null }).pipe(
      tap(() => this.s.success(this.t.instant('Projects.Stages.unassignTeam.unassignSuccess'))),
      catchError(() => {
        this.s.error(this.t.instant('Projects.Stages.unassignTeam.unassignError'));
        return EMPTY;
      })
    );
  }

  assignTeamToStage(stageId: number, teamId: number) {
    const url = `${Config.API}/project/pipeline/timeline-change/${stageId}/team`;
    return this.patch(url, { teamId }).pipe(
      tap(() => this.s.success(this.t.instant('ProjectResources.assignTeam.assignSuccess'))),
      catchError(() => {
        this.s.error(this.t.instant('ProjectResources.assignTeam.assignError'));
        return EMPTY;
      })
    );
  }

  toggleTeamSearchForStage(projectId: number, timelineId: number, isTeamSearch: boolean) {
    const url = `${Config.API}/project/${projectId}/timeline/${timelineId}/team-search`;
    const successKey = isTeamSearch
      ? 'ProjectResources.teamFinder.assignSuccess'
      : 'ProjectResources.teamFinder.unassignSuccess';
    const errorKey = isTeamSearch
      ? 'ProjectResources.teamFinder.assignError'
      : 'ProjectResources.teamFinder.unassignError';

    return this.patch(url, { isTeamSearch }).pipe(
      tap(() => this.s.success(this.t.instant(successKey))),
      catchError(() => {
        this.s.error(this.t.instant(errorKey));
        return EMPTY;
      })
    );
  }

  assignTeamToProjectEntry(projectTimelineId: number, entryId: number, teamId: number | null) {
    const url = `${Config.API}/project/pipeline/${projectTimelineId}/entry/${entryId}/team`;
    const successKey = teamId
      ? 'ProjectResources.assignTeam.assignSuccess'
      : 'ProjectResources.assignTeam.unassignSuccess';
    const errorKey = teamId
      ? 'ProjectResources.assignTeam.assignError'
      : 'ProjectResources.assignTeam.unassignError';

    return this.patch(url, { teamId }).pipe(
      tap(() => this.s.success(this.t.instant(successKey))),
      catchError(() => {
        this.s.error(this.t.instant(errorKey));
        return EMPTY;
      })
    );
  }

  toggleTeamSearchForProjectEntry(projectTimelineId: number, entryId: number, isTeamSearch: boolean) {
    const url = `${Config.API}/project/pipeline/${projectTimelineId}/entry/${entryId}/team-search`;
    const successKey = isTeamSearch
      ? 'ProjectResources.teamFinder.assignSuccess'
      : 'ProjectResources.teamFinder.unassignSuccess';
    const errorKey = isTeamSearch
      ? 'ProjectResources.teamFinder.assignError'
      : 'ProjectResources.teamFinder.unassignError';

    return this.patch(url, { isTeamSearch }).pipe(
      tap(() => this.s.success(this.t.instant(successKey))),
      catchError(() => {
        this.s.error(this.t.instant(errorKey));
        return EMPTY;
      })
    );
  }

  changeTimelineName(timelineId: number, name: string) {
    const url = `${Config.API}/project/pipeline/${timelineId}/name`;
    return this.patch(url, { name });
  }

  getTeamsList() {
    const url = `${Config.API}/project/pipeline/resource/team`;
    return this.get(url);
  }

  updateTeamsOrder(teams: Array<{ teamId: number; sortOrder: number }>) {
    const url = `${Config.API}/project/pipeline/resource/team/order`;
    return this.patch(url, { teams });
  }

  addForecastProject(projectId: number) {
    const url = `${Config.API}/project/pipeline/entry/forecast`;
    return this.put(url, { projectId });
  }
}
